import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STORAGE_KEY = "tandem_user_name";

/**
 * Pri prvom spustení sa zobrazí modálne okno s otázkou na meno.
 *
 * Meno sa uloží do `localStorage[tandem_user_name]` a axios interceptor
 * ho pošle ako `X-Autor` HTTP hlavičku pre audit log (R1 nemá auth).
 * Dialog nemá close-bez-vyplnenia — user musí zadať meno.
 *
 * V R2 sa to nahradí auth-om (Microsoft Entra / Google Workspace).
 */
export function UserNameDialog(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored || !stored.trim()) {
      setOpen(true);
    }
  }, []);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    window.localStorage.setItem(STORAGE_KEY, trimmed);
    setOpen(false);
    // Trigger header re-render — najjednoduchšie cez reload
    window.dispatchEvent(new Event("tandem-user-name-changed"));
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Bránime zatvoreniu cez Escape / overlay click — meno je povinné
        if (!next) return;
        setOpen(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Vitaj v Tandem Rozpočte</DialogTitle>
          <DialogDescription>
            Zadaj svoje meno. Použijeme ho v zázname zmien (kto kedy čo
            upravil) — zmeny v zákazkách budú podpísané týmto menom.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-name">Meno</Label>
            <Input
              id="user-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="napr. Ján Kováč"
              maxLength={100}
              required
              aria-required="true"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!name.trim()}>
              Pokračovať
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
